import { useContext, useMemo } from 'react';

import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  useGetWorkflowHistoryQuery,
  WorkflowHistoryEntry,
} from '@/app/services/workflowHistory';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PrimaryContext } from '@/services';

type WorkflowHistoryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityId: string | null;
  entityName?: string;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export function WorkflowHistoryDialog({
  open,
  onOpenChange,
  entityId,
  entityName,
}: WorkflowHistoryDialogProps) {
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);

  const subtitleLabel = entityName?.trim() ? entityName : entityId;

  const {
    data: history = [],
    isFetching,
    isError,
  } = useGetWorkflowHistoryQuery(entityId ?? '', {
    skip: !open || !entityId,
  });

  const colDefs = useMemo<ColDef<WorkflowHistoryEntry>[]>(
    () => [
      {
        field: 'workflowName',
        headerName: t('workflowHistory.workflowName'),
        flex: 2,
        minWidth: 160,
      },
      {
        field: 'status',
        headerName: t('workflowHistory.status'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'startedAt',
        headerName: t('workflowHistory.startedAt'),
        flex: 1,
        minWidth: 160,
        valueFormatter: (p: ValueFormatterParams) =>
          formatDateTime(p.value as string | null),
      },
      {
        field: 'completedAt',
        headerName: t('workflowHistory.completedAt'),
        flex: 1,
        minWidth: 160,
        valueFormatter: (p: ValueFormatterParams) =>
          formatDateTime(p.value as string | null),
      },
      {
        field: 'errorMessage',
        headerName: t('workflowHistory.error'),
        flex: 2,
        minWidth: 160,
        valueFormatter: (p: ValueFormatterParams) =>
          (p.value as string | null) ?? '—',
      },
    ],
    [t],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('workflowHistory.title')}</DialogTitle>
          {subtitleLabel && (
            <p className="text-muted-foreground text-sm">
              {t('workflowHistory.subtitlePrefix')} <b>{subtitleLabel}</b>
            </p>
          )}
        </DialogHeader>
        <DialogBody>
          {isFetching ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 p-6 text-sm">
              <Loader2Icon className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : isError ? (
            <div className="text-destructive flex items-center gap-2 rounded-lg border p-4 text-sm">
              <AlertCircleIcon className="h-4 w-4 shrink-0" />
              {t('workflowHistory.loadError')}
            </div>
          ) : (
            <div
              className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-96 w-full`}
            >
              <AgGridReact<WorkflowHistoryEntry>
                columnDefs={colDefs}
                rowData={history}
                headerHeight={32}
                rowHeight={28}
                pagination
                paginationPageSize={10}
                suppressMovableColumns
                suppressContextMenu
              />
            </div>
          )}
        </DialogBody>
        <DialogFooter showCloseButton />
      </DialogContent>
    </Dialog>
  );
}
